import { todayIn } from '@brain/core/planning'
import { Button, Card, ListRow, Segmented, toast } from '@brain/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Download, KeyRound, RotateCcw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiCard } from '../../components/settings/AiCard.tsx'
import { CalendarCard } from '../../components/settings/CalendarCard.tsx'
import { CopilotCard } from '../../components/settings/CopilotCard.tsx'
import { DayPlanCard } from '../../components/settings/DayPlanCard.tsx'
import { ErrorLogCard } from '../../components/settings/ErrorLogCard.tsx'
import { GitSourcesCard } from '../../components/settings/GitSourcesCard.tsx'
import { IntegrationsCard } from '../../components/settings/IntegrationsCard.tsx'
import { McpClientsCard } from '../../components/settings/McpClientsCard.tsx'
import { PlanningCard } from '../../components/settings/PlanningCard.tsx'
import { ReviewCard } from '../../components/settings/ReviewCard.tsx'
import { TimeTrackingCard } from '../../components/settings/TimeTrackingCard.tsx'
import { api, meQuery } from '../../lib/api.ts'
import { authClient } from '../../lib/auth.ts'
import { downloadBlob } from '../../lib/download.ts'
import { notesQuery, useNoteAction } from '../../lib/hooks.ts'
import { clearSessionToken } from '../../lib/session-token.ts'
import { applyThemePref, readThemePref, type ThemePref } from '../../lib/theme.ts'

export const Route = createFileRoute('/_app/einstellungen')({
  validateSearch: (s: Record<string, unknown>): { passkey?: string } =>
    typeof s.passkey === 'string' ? { passkey: s.passkey } : {},
  component: Settings,
})

interface PasskeyRow {
  id: string
  name?: string | null
  createdAt?: string | Date | null
}

const passkeysQuery = {
  queryKey: ['passkeys'],
  queryFn: async (): Promise<PasskeyRow[]> => {
    const { data, error } = await authClient.passkey.listUserPasskeys()
    if (error) throw new Error(error.message)
    return (data ?? []) as PasskeyRow[]
  },
}

function Settings() {
  const { t } = useTranslation()
  const { me } = Route.useRouteContext()
  const search = Route.useSearch()
  const router = useRouter()
  const queryClient = useQueryClient()
  const passkeys = useQuery(passkeysQuery)
  const [theme, setTheme] = useState<ThemePref>(() => readThemePref(globalThis.localStorage))
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const add = useMutation({
    mutationFn: async () => {
      const res = await authClient.passkey.addPasskey({ name: navigator.platform || undefined })
      if (res?.error) throw new Error(res.error.message)
    },
    onSuccess: () => {
      setMessage({ ok: true, text: t('settings.passkeyAdded') })
      void queryClient.invalidateQueries({ queryKey: passkeysQuery.queryKey })
    },
    onError: () => setMessage({ ok: false, text: t('settings.passkeyError') }),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await authClient.passkey.deletePasskey({ id })
      if (error) throw new Error(error.message)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: passkeysQuery.queryKey }),
  })

  async function signOut() {
    await authClient.signOut().catch(() => null)
    await clearSessionToken()
    queryClient.setQueryData(meQuery.queryKey, null)
    queryClient.clear()
    router.history.push('/login')
  }

  const showPrompt = passkeys.data?.length === 0 || search.passkey === 'hinzufuegen'
  const dateFmt = new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeZone: me.timezone || 'Europe/Berlin',
  })

  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div data-tauri-drag-region className="hidden h-6 md:block" />
      <div className="mx-auto flex max-w-2xl flex-col gap-4 px-4 py-4 md:px-6">
        <h1 className="text-[22px] text-text-1">{t('settings.title')}</h1>

        <Card className="p-6">
          <h2 className="meta-label">{t('settings.profile')}</h2>
          <p className="mt-3 text-[15px] text-text-1">{me.name}</p>
          <p className="font-mono text-[13px] text-meta">{me.email}</p>
        </Card>

        <Card className="p-6">
          <h2 className="meta-label">{t('settings.passkeys')}</h2>
          {showPrompt && passkeys.data?.length === 0 && (
            <p className="mt-3 rounded-tile border-apricot border-l-4 bg-apricot-bg px-4 py-3 text-[14px] text-text-1">
              {t('settings.addPasskeyPrompt')}
            </p>
          )}
          <ul className="mt-3 flex flex-col gap-1">
            {passkeys.isPending && <li className="text-[13px] text-meta">{t('common.loading')}</li>}
            {passkeys.data?.length === 0 && (
              <li className="text-[13px] text-meta">{t('settings.passkeysEmpty')}</li>
            )}
            {passkeys.data?.map((p) => (
              <li key={p.id} className="flex items-center gap-3 rounded-tile px-3 py-2 hover:bg-grey-1">
                <KeyRound size={16} strokeWidth={1.9} className="text-meta" />
                <span className="flex-1">
                  <span className="block text-[14px] text-text-1">
                    {p.name || t('settings.unnamedPasskey')}
                  </span>
                  {p.createdAt && (
                    <span className="block text-[12px] text-meta">
                      {t('settings.createdAt', { date: dateFmt.format(new Date(p.createdAt)) })}
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size={32}
                  onClick={() => remove.mutate(p.id)}
                  aria-label={t('settings.removePasskey')}
                >
                  <Trash2 size={16} strokeWidth={1.9} />
                </Button>
              </li>
            ))}
          </ul>
          <Button
            variant={showPrompt ? 'primary' : 'secondary'}
            className="mt-4"
            onClick={() => add.mutate()}
            disabled={add.isPending}
          >
            <KeyRound size={16} strokeWidth={1.9} />
            {t('settings.addPasskey')}
          </Button>
          {message && (
            <p
              role="status"
              className={message.ok ? 'mt-3 text-[13px] text-green-fg' : 'mt-3 text-[13px] text-apricot-fg'}
            >
              {message.text}
            </p>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="meta-label">{t('settings.appearance')}</h2>
          <Segmented
            className="mt-3"
            aria-label={t('settings.appearance')}
            value={theme}
            onValueChange={(v) => {
              setTheme(v)
              applyThemePref(v, document.documentElement, globalThis.localStorage)
            }}
            options={[
              { value: 'light', label: t('settings.themeLight') },
              { value: 'dark', label: t('settings.themeDark') },
              { value: 'system', label: t('settings.themeSystem') },
            ]}
          />
        </Card>

        <CalendarCard />
        <DayPlanCard />
        <TimeTrackingCard />
        <McpClientsCard />
        <IntegrationsCard />
        <GitSourcesCard />
        <AiCard />
        <CopilotCard />
        <PlanningCard />
        <ReviewCard />
        <ExportCard />
        <TrashCard />
        <ErrorLogCard />

        <div>
          <Button variant="danger" onClick={signOut}>
            {t('settings.signOut')}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Full export (§19.11): ZIP with Markdown per note, JSON for structured data, attachments. */
function ExportCard() {
  const { t } = useTranslation()
  const exp = useMutation({
    mutationFn: async () => {
      const blob = await api.blob('/v1/export')
      const date = todayIn()
      await downloadBlob(blob, `voluro-brain-export-${date}.zip`)
    },
    onError: () => toast(t('common.error'), { tone: 'attention' }),
  })
  return (
    <Card className="p-6">
      <h2 className="meta-label">{t('settings.export')}</h2>
      <p className="mt-3 text-[14px] text-text-2">{t('settings.exportText')}</p>
      <Button className="mt-4" onClick={() => exp.mutate()} disabled={exp.isPending}>
        <Download size={16} strokeWidth={1.9} />
        {exp.isPending ? t('settings.exporting') : t('settings.exportAll')}
      </Button>
    </Card>
  )
}

function TrashRow({ id, title }: { id: string; title: string }) {
  const { t } = useTranslation()
  const action = useNoteAction(id)
  return (
    <ListRow
      title={title || t('note.untitled')}
      trailing={
        <Button size={32} variant="ghost" onClick={() => action.mutate({ kind: 'restore' })}>
          <RotateCcw size={14} />
          {t('note.restore')}
        </Button>
      }
    />
  )
}

/** Papierkorb: soft-deleted notes, restorable for 30 days (§6.3). */
function TrashCard() {
  const { t } = useTranslation()
  const trash = useQuery(notesQuery({ trash: true, limit: 100 }))
  const items = trash.data?.items ?? []
  return (
    <Card className="p-6">
      <h2 className="meta-label">{t('settings.trash')}</h2>
      <p className="mt-3 text-[14px] text-text-2">{t('settings.trashText')}</p>
      <div className="mt-2 flex flex-col">
        {items.length === 0 && <p className="py-2 text-[13px] text-meta">{t('settings.trashEmpty')}</p>}
        {items.map((n) => (
          <TrashRow key={n.id} id={n.id} title={n.title} />
        ))}
      </div>
    </Card>
  )
}
